/**
 * Rate Limiter for RTDB Cache
 * Protects against API abuse and quota exhaustion
 */

class RateLimiter {
  constructor() {
    this.requests = new Map(); // Track requests per key
    this.userRequests = new Map(); // Track requests per user
    this.globalRequests = [];
    this.suspiciousPatterns = new Map();

    // Configuration
    this.config = {
      // Per-endpoint limits
      maxRequestsPerMinute: 200,  // Increased from 60
      maxRequestsPerHour: 1000,   // Increased from 500
      maxRequestsPerDay: 5000,

      // Per-user limits
      maxUserRequestsPerMinute: 500,  // High limit for page loads with many components
      maxUserRequestsPerHour: 5000,   // Allow for normal browsing and multiple refreshes

      // Burst protection
      burstWindowMs: 10000, // 10 seconds
      maxBurstRequests: 50,  // Increased from 20 for homepage load

      // Pattern detection
      suspiciousThreshold: 500, // Increased from 100 - homepage can legitimately make many requests
      blockDuration: 60 * 60 * 1000, // 1 hour block

      // Quota management
      dailyQuota: 10000,
      monthlyQuota: 100000,
      quotaWarningThreshold: 0.8 // Warn at 80% usage
    };

    // Current quota usage (should be persisted)
    this.quotaUsage = {
      daily: 0,
      monthly: 0,
      lastReset: {
        daily: Date.now(),
        monthly: Date.now()
      }
    };

    // Blocked IPs/users
    this.blockedEntities = new Map();

    // Start cleanup interval
    this.startCleanup();
  }

  /**
   * Check if request should be allowed
   */
  async shouldAllowRequest(key, userId = 'anonymous') {
    const now = Date.now();

    // Check if entity is blocked
    if (this.isBlocked(userId)) {
      console.warn(`🚫 Blocked entity attempting request: ${userId}`);
      return {
        allowed: false,
        reason: 'blocked',
        retryAfter: this.getBlockExpiry(userId)
      };
    }

    // Reset quotas if needed
    this.resetQuotasIfNeeded(now);

    // Check quota limits
    if (this.quotaUsage.daily >= this.config.dailyQuota) {
      console.error('📊 Daily quota exhausted!');
      return {
        allowed: false,
        reason: 'daily_quota_exceeded',
        retryAfter: this.getNextDailyReset()
      };
    }

    if (this.quotaUsage.monthly >= this.config.monthlyQuota) {
      console.error('📊 Monthly quota exhausted!');
      return {
        allowed: false,
        reason: 'monthly_quota_exceeded',
        retryAfter: this.getNextMonthlyReset()
      };
    }

    // Track global request
    this.globalRequests.push(now);

    // Track per-key request
    if (!this.requests.has(key)) {
      this.requests.set(key, []);
    }
    this.requests.get(key).push(now);

    // Track per-user request
    if (!this.userRequests.has(userId)) {
      this.userRequests.set(userId, []);
    }
    this.userRequests.get(userId).push(now);

    // Check for suspicious patterns
    this.detectSuspiciousActivity(userId);

    // Check rate limits
    const keyLimitCheck = this.checkRateLimit(this.requests.get(key), now);
    if (!keyLimitCheck.allowed) {
      return {
        allowed: false,
        reason: 'rate_limit_per_endpoint',
        retryAfter: keyLimitCheck.retryAfter
      };
    }

    const userLimitCheck = this.checkUserRateLimit(this.userRequests.get(userId), now);
    if (!userLimitCheck.allowed) {
      return {
        allowed: false,
        reason: 'rate_limit_per_user',
        retryAfter: userLimitCheck.retryAfter
      };
    }

    // Check burst protection
    const recentRequests = this.globalRequests.filter(
      time => now - time < this.config.burstWindowMs
    );
    if (recentRequests.length > this.config.maxBurstRequests) {
      return {
        allowed: false,
        reason: 'burst_protection',
        retryAfter: now + this.config.burstWindowMs
      };
    }

    // Increment quota usage
    this.quotaUsage.daily++;
    this.quotaUsage.monthly++;

    // Warn if approaching limits
    this.checkQuotaWarnings();

    return { allowed: true };
  }

  /**
   * Check rate limit for request array
   */
  checkRateLimit(requests, now) {
    // Clean old requests
    const oneMinuteAgo = now - 60000;
    const oneHourAgo = now - 3600000;

    const recentMinute = requests.filter(time => time > oneMinuteAgo);
    const recentHour = requests.filter(time => time > oneHourAgo);

    if (recentMinute.length >= this.config.maxRequestsPerMinute) {
      return {
        allowed: false,
        retryAfter: recentMinute[0] + 60000
      };
    }

    if (recentHour.length >= this.config.maxRequestsPerHour) {
      return {
        allowed: false,
        retryAfter: recentHour[0] + 3600000
      };
    }

    return { allowed: true };
  }

  /**
   * Check user-specific rate limits
   */
  checkUserRateLimit(requests, now) {
    const oneMinuteAgo = now - 60000;
    const oneHourAgo = now - 3600000;

    const recentMinute = requests.filter(time => time > oneMinuteAgo);
    const recentHour = requests.filter(time => time > oneHourAgo);

    if (recentMinute.length >= this.config.maxUserRequestsPerMinute) {
      return {
        allowed: false,
        retryAfter: recentMinute[0] + 60000
      };
    }

    if (recentHour.length >= this.config.maxUserRequestsPerHour) {
      return {
        allowed: false,
        retryAfter: recentHour[0] + 3600000
      };
    }

    return { allowed: true };
  }

  /**
   * Detect suspicious activity patterns
   */
  detectSuspiciousActivity(userId) {
    const now = Date.now();
    const fiveMinutesAgo = now - 300000;

    const userReqs = this.userRequests.get(userId) || [];
    const recentRequests = userReqs.filter(time => time > fiveMinutesAgo);

    if (recentRequests.length > this.config.suspiciousThreshold) {
      console.warn(`🚨 Suspicious activity detected for user: ${userId}`);
      console.warn(`   ${recentRequests.length} requests in last 5 minutes`);

      // Track pattern
      if (!this.suspiciousPatterns.has(userId)) {
        this.suspiciousPatterns.set(userId, []);
      }
      this.suspiciousPatterns.get(userId).push(now);

      // Auto-block if repeated suspicious activity
      const patterns = this.suspiciousPatterns.get(userId);
      const recentPatterns = patterns.filter(time => now - time < 3600000); // 1 hour

      if (recentPatterns.length >= 3) {
        this.blockEntity(userId, 'repeated_suspicious_activity');
      }
    }
  }

  /**
   * Block an entity
   */
  blockEntity(entityId, reason) {
    const expiry = Date.now() + this.config.blockDuration;
    this.blockedEntities.set(entityId, {
      reason,
      expiry,
      timestamp: Date.now()
    });

    console.error(`🔒 Blocked entity: ${entityId} - Reason: ${reason}`);

    // Could also persist this to database
  }

  /**
   * Check if entity is blocked
   */
  isBlocked(entityId) {
    const block = this.blockedEntities.get(entityId);
    if (!block) return false;

    if (Date.now() > block.expiry) {
      this.blockedEntities.delete(entityId);
      return false;
    }

    return true;
  }

  /**
   * Get block expiry time
   */
  getBlockExpiry(entityId) {
    const block = this.blockedEntities.get(entityId);
    return block ? block.expiry : null;
  }

  /**
   * Reset quotas if needed
   */
  resetQuotasIfNeeded(now) {
    // Daily reset (at midnight)
    const lastDailyReset = new Date(this.quotaUsage.lastReset.daily);
    const today = new Date(now);

    if (today.getDate() !== lastDailyReset.getDate()) {
      this.quotaUsage.daily = 0;
      this.quotaUsage.lastReset.daily = now;
      console.log('📊 Daily quota reset');
    }

    // Monthly reset
    const lastMonthlyReset = new Date(this.quotaUsage.lastReset.monthly);

    if (today.getMonth() !== lastMonthlyReset.getMonth()) {
      this.quotaUsage.monthly = 0;
      this.quotaUsage.lastReset.monthly = now;
      console.log('📊 Monthly quota reset');
    }
  }

  /**
   * Check and warn about quota usage
   */
  checkQuotaWarnings() {
    const dailyPercent = this.quotaUsage.daily / this.config.dailyQuota;
    const monthlyPercent = this.quotaUsage.monthly / this.config.monthlyQuota;

    if (dailyPercent >= this.config.quotaWarningThreshold) {
      console.warn(`⚠️ Daily quota at ${(dailyPercent * 100).toFixed(1)}%`);
    }

    if (monthlyPercent >= this.config.quotaWarningThreshold) {
      console.warn(`⚠️ Monthly quota at ${(monthlyPercent * 100).toFixed(1)}%`);
    }
  }

  /**
   * Get next daily reset time
   */
  getNextDailyReset() {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow.getTime();
  }

  /**
   * Get next monthly reset time
   */
  getNextMonthlyReset() {
    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    nextMonth.setDate(1);
    nextMonth.setHours(0, 0, 0, 0);
    return nextMonth.getTime();
  }

  /**
   * Cleanup old request data
   */
  startCleanup() {
    setInterval(() => {
      const now = Date.now();
      const oneHourAgo = now - 3600000;

      // Clean global requests
      this.globalRequests = this.globalRequests.filter(time => time > oneHourAgo);

      // Clean per-key requests
      for (const [key, requests] of this.requests.entries()) {
        const filtered = requests.filter(time => time > oneHourAgo);
        if (filtered.length === 0) {
          this.requests.delete(key);
        } else {
          this.requests.set(key, filtered);
        }
      }

      // Clean per-user requests
      for (const [userId, requests] of this.userRequests.entries()) {
        const filtered = requests.filter(time => time > oneHourAgo);
        if (filtered.length === 0) {
          this.userRequests.delete(userId);
        } else {
          this.userRequests.set(userId, filtered);
        }
      }

      // Clean expired blocks
      for (const [entityId, block] of this.blockedEntities.entries()) {
        if (now > block.expiry) {
          this.blockedEntities.delete(entityId);
        }
      }
    }, 60000); // Every minute
  }

  /**
   * Get current status
   */
  getStatus() {
    return {
      quota: {
        daily: {
          used: this.quotaUsage.daily,
          limit: this.config.dailyQuota,
          percent: ((this.quotaUsage.daily / this.config.dailyQuota) * 100).toFixed(1)
        },
        monthly: {
          used: this.quotaUsage.monthly,
          limit: this.config.monthlyQuota,
          percent: ((this.quotaUsage.monthly / this.config.monthlyQuota) * 100).toFixed(1)
        }
      },
      activeRequests: {
        total: this.globalRequests.length,
        byEndpoint: this.requests.size,
        byUser: this.userRequests.size
      },
      blocked: {
        count: this.blockedEntities.size,
        entities: Array.from(this.blockedEntities.keys())
      },
      suspicious: {
        count: this.suspiciousPatterns.size,
        users: Array.from(this.suspiciousPatterns.keys())
      }
    };
  }

  /**
   * Manual unblock
   */
  unblock(entityId) {
    this.blockedEntities.delete(entityId);
    console.log(`🔓 Unblocked entity: ${entityId}`);
  }

  /**
   * Reset all limits (use with caution)
   */
  reset() {
    this.requests.clear();
    this.userRequests.clear();
    this.globalRequests = [];
    this.suspiciousPatterns.clear();
    console.log('🔄 Rate limiter reset');
  }
}

// Singleton instance
const rateLimiter = new RateLimiter();

// Export for debugging
if (typeof window !== 'undefined') {
  window.RateLimiter = {
    status: () => rateLimiter.getStatus(),
    unblock: (id) => rateLimiter.unblock(id),
    reset: () => rateLimiter.reset(),
    config: rateLimiter.config
  };
}

export default rateLimiter;